import { css } from '@firebolt-dev/css'
import {
  BoxIcon,
  BrickWallIcon,
  CatIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CodeIcon,
  CopyIcon,
  FilePlusCornerIcon,
  GemIcon,
  HamIcon,
  HammerIcon,
  ImageIcon,
  Music2Icon,
  PersonStandingIcon,
  SearchIcon,
  ShirtIcon,
  Trash2Icon,
  UploadIcon,
  Volume2Icon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { cls } from '../core/cls'
import { FileArea } from './FileArea'
import { UIInspector } from './UIInspector'
import { useRegistry } from './useRegistry'

const types = {
  block: {
    name: 'block',
    title: 'Blocks',
    icon: BoxIcon,
    editor: 'model',
    mime: null,
  },
  item: {
    name: 'item',
    title: 'Items',
    icon: GemIcon,
    editor: 'model',
    mime: null,
  },
  tool: {
    name: 'tool',
    title: 'Tools',
    icon: HammerIcon,
    editor: 'model',
    mime: null,
  },
  consumable: {
    name: 'consumable',
    title: 'Consumables',
    icon: HamIcon,
    editor: 'model',
    mime: null,
  },
  wearable: {
    name: 'wearable',
    title: 'Wearables',
    icon: ShirtIcon,
    editor: 'model',
    mime: null,
  },
  entity: {
    name: 'entity',
    title: 'Entities',
    icon: CatIcon,
    editor: 'model',
    mime: null,
  },
  body: {
    name: 'body',
    title: 'Bodies',
    icon: PersonStandingIcon,
    editor: 'model',
    mime: null,
  },
  script: {
    name: 'script',
    title: 'Scripts',
    icon: CodeIcon,
    editor: 'code',
    mime: null,
  },
  audio: {
    name: 'audio',
    title: 'Audio',
    icon: Music2Icon,
    editor: 'file',
    mime: 'audio/mp3',
  },
  sound: {
    name: 'sound',
    title: 'Sounds',
    icon: Volume2Icon,
    editor: 'inspect',
    mime: null,
  },
  texture: {
    name: 'texture',
    title: 'Textures',
    icon: ImageIcon,
    editor: 'file',
    mime: 'image/png',
  },
}

const allTypes = [
  types.block,
  types.item,
  types.tool,
  types.consumable,
  types.wearable,
  types.entity,
  types.body,
  types.script,
  types.audio,
  types.sound,
  types.texture,
]

export function UIExplorer({ client, onNew, onOpen, onDuplicate, onRemove, showModal }) {
  const [type, setType] = useState(types.block)
  const [inspect, setInspect] = useState(null)

  const [registry] = useRegistry(client)

  const asset = inspect ? registry.get(inspect.type, inspect.id) : null

  return (
    <div
      className='explorer'
      css={css`
        flex: 1;
        display: flex;
        align-items: stretch;
        gap: 10px;
        .explorer-types {
          display: flex;
          flex-direction: column;
        }
        .explorer-type {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 5px;
          margin: 0 0 4px;
          &:hover {
            cursor: pointer;
          }
          &:hover:not(.active) {
            background: var(--bg2);
          }
          &.active {
            background: var(--bg3);
          }
        }
        .explorer-content {
          width: 300px;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
          > * {
            flex: 1;
          }
        }
      `}
    >
      <div className='explorer-types'>
        {allTypes.map(t => (
          <div
            key={t.name}
            className={cls('explorer-type', { active: type === t })}
            onClick={() => {
              setType(type === t ? null : t)
            }}
          >
            <Icon icon={t.icon} size={20} />
          </div>
        ))}
      </div>
      {type && (
        <div className='explorer-content'>
          <List
            client={client}
            type={type}
            onNew={onNew}
            onSelect={asset => {
              if (types[asset.type].editor === 'inspect') {
                setInspect({ type: asset.type, id: asset.id })
              } else {
                onOpen(asset)
              }
            }}
            onDuplicate={onDuplicate}
            onRemove={onRemove}
          />
          {asset && (
            <UIInspector
              key={inspect}
              client={client}
              asset={asset}
              onChangeID={newID => {
                setInspect({ type: inspect.type, id: newID })
              }}
              onClose={() => setInspect(null)}
              showModal={showModal}
            />
          )}
        </div>
      )}
    </div>
  )
}

function List({ client, type, onNew, onSelect, onDuplicate, onRemove }) {
  const [query, setQuery] = useState('')
  const [registry, registryV] = useRegistry(client)

  const assets = useMemo(() => {
    const q = query.toLowerCase()
    const ignored = new Set(['air', 'unknown'])
    const assets = registry.raw.filter(asset => {
      if (asset.type !== type.name) return false
      if (ignored.has(asset.id)) return false
      return asset.id.toLowerCase().includes(q)
    })
    assets.sort((a, b) => a.id.localeCompare(b.id)) // name asc
    return assets
  }, [type, query, registryV])

  console.log('assets', assets)

  const isMimeType = type.mime

  return (
    <div
      className='list noscrollbar'
      css={css`
        flex: 1;
        background: var(--bg3);
        border-radius: 5px;
        padding: 15px;
        overflow-y: auto;
        .list-title {
          display: flex;
          align-items: center;
          margin: 0 0 10px;
          span {
            flex: 1;
            font-size: 20px;
            font-weight: 500;
          }
        }
        .list-new {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 5px;
          position: relative; // for FileArea
          &:hover {
            cursor: pointer;
            background: var(--bg4);
          }
        }
        .list-search {
          display: flex;
          align-items: center;
          padding: 0 10px;
          background: var(--bg4);
          border-radius: 5px;
          height: 34px;
          margin: 0 0 10px;
          &:focus-within {
            background: var(--bg5);
          }
          svg {
            margin-right: 5px;
          }
          input {
            flex: 1;
            width: 100%;
          }
        }
        .list-asset {
          display: flex;
          align-items: center;
          padding: 4px 10px 4px 4px;
          border-radius: 5px;
          &-icon {
            width: 40px;
            height: 40px;
            border-radius: 5px;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            /* background-color: var(--bg2); */
            margin-right: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          &-id {
            flex: 1;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            margin: 0 0 3px;
          }
          &-btns {
            display: none;
            align-items: center;
          }
          &-btn {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 8px;
            color: #4f4f66;
            &:hover {
              cursor: pointer;
              color: white;
            }
          }
          &:hover {
            cursor: pointer;
            background: var(--bg4);
            .list-asset-btns {
              display: flex;
            }
          }
        }
      `}
    >
      <div className='list-title'>
        <span>{type.title}</span>
        {isMimeType && (
          <label className='list-new'>
            <UploadIcon size={18} />
            <FileArea
              accept={type.mime}
              onFile={file => {
                onNew({ type: type.name, file })
              }}
            />
          </label>
        )}
        {!isMimeType && (
          <div
            className='list-new'
            onClick={() => {
              console.log('todo: sounds should be an upload button')
              onNew({ type: type.name })
            }}
          >
            <FilePlusCornerIcon size={18} />
          </div>
        )}
      </div>
      <label className='list-search'>
        <SearchIcon size={18} />
        <input type='text' value={query} onChange={e => setQuery(e.target.value)} />
      </label>
      {assets.map(asset => (
        <div
          key={`${asset.type}.${asset.id}`}
          className='list-asset'
          onClick={e => {
            if (e.defaultPrevented) return
            onSelect(asset)
          }}
        >
          <ListIcon registry={registry} asset={asset} />
          <div className='list-asset-id'>{asset.id}</div>
          <div className='list-asset-btns'>
            {!isMimeType && (
              <div
                className='list-asset-btn'
                onClick={e => {
                  e.preventDefault()
                  onDuplicate(asset)
                }}
              >
                <CopyIcon size={16} />
              </div>
            )}
            <div
              className='list-asset-btn'
              onClick={e => {
                e.preventDefault()
                onRemove(asset)
              }}
            >
              <Trash2Icon size={16} />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function Icon({ icon: Ico, ...props }) {
  if (!Ico) return null
  return <Ico {...props} />
}

function ListIcon({ registry, asset }) {
  if (types[asset.type].editor === 'model') {
    return (
      <div
        className='list-asset-icon'
        css={css`
          background-image: url(${registry.get('icon', asset.icon)?.url});
        `}
      />
    )
  }
  return (
    <div className='list-asset-icon'>
      <Icon icon={types[asset.type].icon} size={20} />
    </div>
  )
}
